import { List, Locate, Map, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FALLBACK_CAM_BOUNDS } from './constants';
import {
    PopulatedShout,
    PopulatedVenue,
    RecommendationStatus,
    User
} from './types';

const UNKNOWN_USER: User = {
    uid: 'unknown',
    displayName: 'Unknown User',
    username: 'unknown',
    photoURL: 'https://via.placeholder.com/150',
    friends: [],
    email: ''
};

import Fuse from 'fuse.js';
import { AddButton } from './components/AddButton';
import { AuthScreen } from './components/AuthScreen';
import { CurrentLocationMarker } from './components/CurrentLocationMarker';
import { Drawer } from './components/Drawer';
import { EmailConfirmationScreen } from './components/EmailConfirmationScreen';
import { PostHogErrorBoundary } from './components/ErrorBoundary';
import { GlobalModals } from './components/GlobalModals';
import { Layout } from './components/Layout';
import { LoadingScreen } from './components/LoadingScreen';
import { MapPin as MapPinComponent } from './components/MapPin';
import { OverlayMarker } from './components/OverlayMarker';
import { VenueList } from './components/VenueList';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PWAProvider } from './contexts/PWAContext';
import { useNotifications } from './hooks/useNotifications';
import { usePostHogIdentify } from './hooks/usePostHogIdentify';
import { initOfflineSync } from './services/posthog';
import { useDataStore } from './stores/useDataStore';
import { useMapStore } from './stores/useMapStore';
import { useUIStore } from './stores/useUIStore';

// Wrapper to provide Auth/PWA contexts
function AppWithProviders() {
    return (
        <PostHogErrorBoundary>
            <AuthProvider>
                <PWAProvider>
                    <MainAppContent />
                </PWAProvider>
            </AuthProvider>
        </PostHogErrorBoundary>
    );
}

// Main App
function MainAppContent() {
  // -- AUTH STATE --
  const { 
      currentUser, 
      session,
      loadingAuth, 
      pendingConfirmationEmail, 
      login, 
      register, 
      logout, 
      updateProfile, 
      setPendingConfirmationEmail
  } = useAuth();

  // -- POSTHOG --
  usePostHogIdentify(session, session?.user ?? null);

  // -- STORES --
  const { 
      venues, shouts, users, goodShouts, 
      loadData, subscribeToDataChanges,
      updateShout, deleteShout, addGoodShout,
      addRecommendation,
      initialized
  } = useDataStore();
  
  const { 
      setMapContainer, mapInstance, isMapError, 
      currentLocation, hasLocationAccess, isUserPanned, 
      requestUserLocation, mapContainer,
      previewPin
  } = useMapStore();
  
  // -- UI STORE --
  const {
      isAddModalOpen, openAddModal, closeAddModal,
      isProfileOpen, openProfile, closeProfile,
      viewMode, setViewMode,
      filters, setFilters,
      selectedVenueId, selectVenue,
      listSearchTerm, setListSearchTerm,
      handlePopState,
      openAdmin, openSupport, openInstallHelp, openWelcome: openWelcomeModal, closeWelcome,
      isAdminPanelOpen, isSupportModalOpen, isInstallHelpOpen, isWelcomeModalOpen, isFilterOpen,
      toast, showToast, hideToast,
      unreadNotificationCount, openNotifications
  } = useUIStore();

  // -- INITIALIZATION & SYNC --

  // 0. Deep Link Handling (after auth)
  // Supported deep links:
  //   - /profile: Opens Profile modal (for friend request notifications)
  // To add new deep links, add path checks below.
  useEffect(() => {
    if (!currentUser || loadingAuth) return;

    const path = window.location.pathname;
        
    // Handle /profile deep link
    if (path === '/profile') {
        openProfile();
        // Clean up URL by replacing with root
        window.history.replaceState({}, '', '/');
    }
  }, [currentUser, loadingAuth, openProfile]);

  // 1. History Sync
  useEffect(() => {
      // Init store with current history
      handlePopState(window.history.state);

      const onPopState = (e: PopStateEvent) => {
          handlePopState(e.state);
      };
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
  }, []); // Run once on mount

  // 2. Data Load & Realtime
  useEffect(() => {
      if (currentUser) {
          loadData(currentUser);
          const unsubscribe = subscribeToDataChanges(currentUser);
          return () => {
              unsubscribe();
          };
      } else {
          // Logged out
          useDataStore.getState().reset();
          useUIStore.getState().reset();
      }
  }, [currentUser]);

  // 3. Location & Errors
  useEffect(() => {
      if (currentUser) {
           requestUserLocation();
      }
  }, [currentUser]);

  // Watch for location and data errors
  const locationError = useMapStore(state => state.locationError);
  const dataError = useDataStore(state => state.error);
  
  useEffect(() => {
      if (locationError) {
          showToast(locationError, 'error');
      }
  }, [locationError]);

  useEffect(() => {
      if (dataError && currentUser) {
          showToast(dataError, 'error', {
              action: {
                  label: 'Retry',
                  onClick: () => useDataStore.getState().loadData(currentUser),
              },
              duration: 10000,
          });
      }
  }, [dataError, currentUser]);

  // 4. Map Init (If logical conditions met)
  useEffect(() => {
      // Use values from store directly to avoid closure staleness if needed, 
      // but relying on props is also fine if deps are correct.
      // Since we have mapContainer in props now, we can use it.
      // But initMap is an action, stable.
      
      const { mapInstance } = useMapStore.getState();
            
      if (mapContainer && !mapInstance && window.google && currentUser && viewMode === 'map') {
           useMapStore.getState().initMap(mapContainer, currentLocation);
      }
  }, [currentUser, viewMode, mapContainer]); 


  // -- NOTIFICATIONS --
  const { permissionStatus, requestPermission } = useNotifications(currentUser);


  // -- OFFLINE DETECTION --
  useEffect(() => {
      const handleOffline = () => showToast("You are offline. Viewing cached data.", 'error');
      const handleOnline = () => showToast("You are back online!", 'info');
      window.addEventListener('offline', handleOffline);
      window.addEventListener('online', handleOnline);
      return () => {
          window.removeEventListener('offline', handleOffline);
          window.removeEventListener('online', handleOnline);
      };
  }, []);

  // -- POSTHOG OFFLINE SYNC --
  useEffect(() => {
      const cleanup = initOfflineSync();
      return cleanup;
  }, []);


  // -- COMPUTED DATA --
  const populatedVenues = useMemo(() => {
    return venues.map(venue => {
        const venueShouts = shouts.filter(s => s.venueId === venue.venueId);
        
        const populatedShouts: PopulatedShout[] = venueShouts.map(s => {
            const shoutGoodShouts = goodShouts.filter(gs => gs.shoutId === s.id);
            return {
                ...s,
                user: users.find(u => u.uid === s.userId) || UNKNOWN_USER,
                goodShouts: shoutGoodShouts.map(gs => ({
                    ...gs,
                    user: users.find(u => u.uid === gs.userId) || UNKNOWN_USER
                })).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) 
            };
        });

        const statuses = populatedShouts.map(s => s.status);
        let primaryStatus = RecommendationStatus.RECOMMENDED;
        if (statuses.includes(RecommendationStatus.SEAL_OF_APPROVAL)) primaryStatus = RecommendationStatus.SEAL_OF_APPROVAL;
        else if (statuses.includes(RecommendationStatus.DO_NOT_RECOMMEND)) primaryStatus = RecommendationStatus.DO_NOT_RECOMMEND;
        else if (statuses.includes(RecommendationStatus.WANT_TO_VISIT) && statuses.length === 1) primaryStatus = RecommendationStatus.WANT_TO_VISIT;
        else if (statuses.includes(RecommendationStatus.ITS_ALRIGHT) && statuses.length === 1) primaryStatus = RecommendationStatus.ITS_ALRIGHT;

        return {
            ...venue,
            shouts: populatedShouts,
            primaryStatus
        } as PopulatedVenue;
    }).filter(v => v.shouts.length > 0);
  }, [venues, shouts, users, goodShouts]);


  // -- FILTERED DATA --
  const filteredVenues = useMemo(() => {
    let results = populatedVenues;

    // 1. Categories
    if (filters.categories.length > 0) {
        results = results.filter(venue => filters.categories.includes(venue.category));
    }

    // 2. Statuses
    if (filters.statuses.length > 0) {
        results = results.filter(venue => venue.shouts.some(s => filters.statuses.includes(s.status)));
    }

    // 3. Friends/Users
    if (filters.userIds.length > 0) {
        results = results.filter(venue => venue.shouts.some(s => filters.userIds.includes(s.userId)));
    }

    // 4. Search Term (Fuzzy Match)
    if (listSearchTerm && viewMode === 'list') {
        const fuse = new Fuse(results, {
            keys: [
                { name: 'name', weight: 0.7 },
                { name: 'shouts.description', weight: 0.3 }
            ],
            threshold: 0.4,
            includeScore: true
        });

        const fuseResults = fuse.search(listSearchTerm);
        return fuseResults.map(result => result.item);
    }

    return results;
  }, [populatedVenues, filters, listSearchTerm, viewMode]);

  const selectedPopulatedVenue = useMemo(() => {
    if (!selectedVenueId) return null;
    return populatedVenues.find(v => v.venueId === selectedVenueId) || null;
  }, [selectedVenueId, populatedVenues]);

  // Auto-deselect if venue disappears (e.g. after deleting last shout)
  // Auto-deselect if venue disappears (e.g. after deleting last shout)
  // We check initialized to ensure we don't deselect during initial load (where venues is empty but potentially loading)
  useEffect(() => {
      if (initialized && selectedVenueId && !selectedPopulatedVenue) {
          selectVenue(null);
      }
  }, [selectedVenueId, selectedPopulatedVenue, initialized, selectVenue]);


  // -- WELCOME LOGIC --
  const [hasSeenWelcome, setHasSeenWelcome] = useState(false);
   const myShoutCount = useMemo(() => {
      if (!currentUser) return 0;
      return shouts.filter(s => s.userId === currentUser.uid).length;
  }, [shouts, currentUser]);

  useEffect(() => {
      if (currentUser && !loadingAuth) {
          const storageKey = `welcome_seen_${currentUser.uid}`;
          const hasSeenLocal = localStorage.getItem(storageKey) === 'true';
          
          if (hasSeenLocal && !hasSeenWelcome) {
              setHasSeenWelcome(true);
              return;
          }

          const hasContent = currentUser.friends.length > 0 || myShoutCount > 0;

          if (hasContent) {
               if (!hasSeenWelcome) {
                   setHasSeenWelcome(true);
                   localStorage.setItem(storageKey, 'true');
               }
               // Note: 'isWelcomeModalOpen' is in store now.
          } else if (!hasSeenWelcome) {
               openWelcomeModal();
          }
      }
  }, [currentUser, loadingAuth, myShoutCount, hasSeenWelcome]);


  // -- HANDLERS --
  const handleLogin = async (email: string, pass: string) => {
    await login(email, pass);
  };

  const handleRegister = async (u: string, p: string, d: string, username: string) => {
    await register(u, p, d, username);
  };

  const handleLogout = async () => {
      await logout();
      closeProfile();
      closeAddModal();
      selectVenue(null);
  };



  const getFallbackPosition = (lat: number, lng: number) => {
    const { minLat, maxLat, minLng, maxLng } = FALLBACK_CAM_BOUNDS;
    const top = ((maxLat - lat) / (maxLat - minLat)) * 100;
    const left = ((lng - minLng) / (maxLng - minLng)) * 100;
    return { top: `${top}%`, left: `${left}%` };
  };

  // -- SELECTION EFFECT (PANNING) --
  useEffect(() => {
      if (selectedVenueId && mapInstance && viewMode === 'map') {
          const venue = populatedVenues.find(v => v.venueId === selectedVenueId);
          if (venue) {
              mapInstance.panTo({ lat: venue.lat, lng: venue.lng });
          }
      }
  }, [selectedVenueId, mapInstance, viewMode, populatedVenues]);


  // -- RENDER CHECKS --

  if (loadingAuth) return <LoadingScreen />;

  if (pendingConfirmationEmail) {
      return <EmailConfirmationScreen 
          email={pendingConfirmationEmail} 
          onLogout={() => {
              setPendingConfirmationEmail(null);
              handleLogout();
          }} 
      />;
  }

  if (currentUser && !currentUser.emailConfirmedAt) {
      return <EmailConfirmationScreen 
          email={currentUser.email || "your email address"} 
          onLogout={handleLogout} 
      />;
  }

  if (!currentUser) {
      return <AuthScreen onLogin={handleLogin} onRegister={handleRegister} />;
  }

  const activeFilterCount = filters.categories.length + filters.statuses.length + filters.userIds.length;

  return (
    <Layout
        currentUser={currentUser}
        viewMode={viewMode}
        unreadNotificationCount={unreadNotificationCount}
        onProfileClick={openProfile}
        onNotificationsClick={openNotifications}
        toastProps={{ message: toast.message, type: toast.type, isVisible: toast.isVisible, onClose: hideToast, action: toast.action, duration: toast.duration }}
        isMapError={isMapError}
    >
       {/* MAIN CONTENT */}
       <div className="w-full h-full relative">
             
             {/* MAP LAYER */}
             <div className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${viewMode === 'map' ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}>
                 {isMapError ? (
                     <div className="w-full h-full bg-brand-cream relative overflow-hidden flex items-center justify-center">
                         <div className="absolute inset-0 opacity-5 pointer-events-none" 
                                 style={{backgroundImage: 'radial-gradient(#374151 1px, transparent 1px)', backgroundSize: '20px 20px'}}>
                         </div>
                         <div className="relative w-full h-full max-w-2xl max-h-2xl">
                             {filteredVenues.map((venue) => {
                                 const pos = getFallbackPosition(venue.lat, venue.lng);
                                 return (
                                     <div key={venue.venueId} className="absolute" style={{ top: pos.top, left: pos.left }}>
                                         <MapPinComponent
                                             status={venue.primaryStatus}
                                             category={venue.category}
                                             count={venue.shouts.length}
                                             isSelected={selectedVenueId === venue.venueId}
                                            onClick={() => selectVenue(venue.venueId)}
                                            name={venue.name}
                                            avatarUrl={(venue.shouts.find(sh => sh.status === venue.primaryStatus) ?? venue.shouts[0])?.user.photoURL}
                                        />
                                     </div>
                                 );
                             })}
                         </div>
                     </div>
                 ) : (
                     <div ref={setMapContainer} className="w-full h-full" id="map">
                         {mapInstance && filteredVenues.map(venue => (
                             <OverlayMarker key={venue.venueId} map={mapInstance} position={{ lat: venue.lat, lng: venue.lng }}>
                                 <MapPinComponent
                                     status={venue.primaryStatus}
                                     category={venue.category}
                                     count={venue.shouts.length}
                                     isSelected={selectedVenueId === venue.venueId}
                                    onClick={() => selectVenue(venue.venueId)}
                                    name={venue.name}
                                    avatarUrl={(venue.shouts.find(sh => sh.status === venue.primaryStatus) ?? venue.shouts[0])?.user.photoURL}
                                />
                             </OverlayMarker>
                         ))}
                         {mapInstance && hasLocationAccess && (
                             <OverlayMarker map={mapInstance} position={currentLocation}>
                                 <CurrentLocationMarker />
                             </OverlayMarker>
                         )}
                         {mapInstance && previewPin && (
                            <OverlayMarker map={mapInstance} position={previewPin}>
                                <div className="flex flex-col items-center -translate-x-1/2 -translate-y-full">
                                    <div className="bg-white px-3 py-1.5 rounded-xl shadow-lg border border-brand-cream-dark text-xs font-bold mb-1 text-brand-dark animate-in fade-in slide-in-from-bottom-2">
                                        {previewPin.name}
                                    </div>
                                    <div className="relative">
                                        <div className="w-4 h-4 bg-brand-red rounded-full shadow-lg relative z-10 border-2 border-white"></div>
                                        <div className="absolute inset-0 bg-brand-red rounded-full animate-ping opacity-75 motion-reduce:animate-none"></div>
                                    </div>
                                </div>
                            </OverlayMarker>
                         )}
                     </div>
                 )}
             </div>

             {/* LIST LAYER */}
             {viewMode === 'list' && (
                 <div className="absolute inset-0 w-full h-full z-20 bg-brand-cream animate-in fade-in slide-in-from-bottom-4 duration-300">
                     <VenueList 
                         venues={filteredVenues}
                         searchTerm={listSearchTerm}
                         setSearchTerm={setListSearchTerm}
                         onSelectVenue={(id) => selectVenue(id)}
                     />
                 </div>
             )}
       </div>

       {/* VIEW TOGGLE */}
       {!selectedVenueId && (
           <div className="absolute bottom-6 inset-x-0 z-40 px-4 flex flex-col items-center gap-3 pointer-events-none">
               {viewMode === 'map' && hasLocationAccess && isUserPanned && (
                   <button
                       onClick={() => {
                           requestUserLocation();
                       }}
                       className="pointer-events-auto bg-white text-brand-dark shadow-lg rounded-full px-4 py-2 flex items-center gap-2 hover:scale-105 transition-transform active:scale-95 border border-brand-cream-dark"
                   >
                       <Locate size={16} className="text-brand-steel" />
                       <span className="font-bold text-xs">Return to Location</span>
                   </button>
               )}

               {/* One bottom control bar: Map | List segments, filter, and the red + */}
               <div className="pointer-events-auto w-full max-w-md flex items-center gap-3">
                   <div className="flex-1 flex items-center gap-1 p-1.5 bg-brand-cream/95 backdrop-blur-sm rounded-full shadow-[0_10px_30px_rgba(55,65,81,0.22)] border border-brand-cream-dark">
                       <button
                           aria-label="Map View"
                           onClick={() => setViewMode('map')}
                           className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-full font-bold text-sm transition-colors ${viewMode === 'map' ? 'bg-brand-red text-white shadow-md' : 'text-brand-dark'}`}
                       >
                           <Map size={18} strokeWidth={2.5} />
                           <span>Map</span>
                       </button>
                       <button
                           aria-label="List View"
                           onClick={() => setViewMode('list')}
                           className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-full font-bold text-sm transition-colors ${viewMode === 'list' ? 'bg-brand-red text-white shadow-md' : 'text-brand-dark'}`}
                       >
                           <List size={18} strokeWidth={2.5} />
                           <span>List</span>
                       </button>
                       <button
                           aria-label="Filter Map"
                           onClick={() => useUIStore.getState().openFilter()}
                           className={`relative w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-colors ${activeFilterCount > 0 ? 'bg-brand-dark text-white' : 'bg-white text-brand-dark border border-brand-cream-dark'}`}
                       >
                           <SlidersHorizontal size={18} strokeWidth={2.5} />
                           {activeFilterCount > 0 && (
                               <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-red text-white text-[9px] font-bold flex items-center justify-center rounded-full border border-brand-cream">
                                   {activeFilterCount}
                               </span>
                           )}
                       </button>
                   </div>

                   <AddButton onClick={openAddModal} />
               </div>
           </div>
       )}

       {/* DRAWER */}
       {selectedVenueId && (
         <Drawer 
             venue={selectedPopulatedVenue} 
             onClose={() => selectVenue(null)}
         />
       )}

      {/* GLOBAL MODALS */}
      <GlobalModals
        permissionStatus={permissionStatus}
        onRequestNotificationPermission={requestPermission}
      />
    </Layout>
  );
}

export default AppWithProviders;
